import { Fragment, Mark, Node, NodeType, MarkType } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { Step } from '@tiptap/pm/transform';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension } from '@tiptap/react';

type TextStyleAttrs = {
  fontSize?: string;
  fontFamily?: string;
  color?: string;
};

type UniformStyles = {
  fontSize?: string;
  fontFamily?: string;
  color?: string;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
};

type StyleProperty = keyof UniformStyles;

type TextStyleMark = Mark & {
  type: MarkType & {
    name: string;
  };
  attrs: TextStyleAttrs;
};

type TextNodeType = Node & {
  text?: string;
  marks: readonly TextStyleMark[];
};

type ParagraphNodeType = Node & {
  type: NodeType & {
    name: string;
  };
  content: Fragment & { content: TextNodeType[] };
  nodeSize: number;
};

type ListItemNodeType = Node & {
  type: NodeType & {
    name: string;
  };
  content: Fragment & { firstChild: ParagraphNodeType };
  nodeSize: number;
  attrs: Record<string, unknown>;
};

type ReplaceStep = {
  slice?: {
    content?: {
      firstChild?: {
        type?: {
          name?: string;
        };
      };
    };
  };
  from?: number;
  to?: number;
} & Step;

const buildStyleString = (styles: UniformStyles): string => {
  const styleArray: string[] = [];

  if (styles.fontSize) styleArray.push(`font-size: ${styles.fontSize}`);
  if (styles.fontFamily) styleArray.push(`font-family: ${styles.fontFamily}`);
  if (styles.color) styleArray.push(`color: ${styles.color}`);
  if (styles.isBold) styleArray.push('font-weight: bold');
  if (styles.isItalic) styleArray.push('font-style: italic');
  if (styles.isUnderline) styleArray.push('text-decoration: underline');

  return styleArray.join(';');
};

const getStylesFromMarks = (marks: readonly Mark[]): UniformStyles => {
  const styles: UniformStyles = {};

  marks.forEach(mark => {
    if (mark.type.name === 'textStyle') {
      const attrs = mark.attrs as TextStyleAttrs;
      if (attrs.fontSize) styles.fontSize = attrs.fontSize;
      if (attrs.fontFamily) styles.fontFamily = attrs.fontFamily;
      if (attrs.color) styles.color = attrs.color;
    } else if (mark.type.name === 'bold') {
      styles.isBold = true;
    } else if (mark.type.name === 'italic' || mark.type.name === 'em') {
      styles.isItalic = true;
    } else if (mark.type.name === 'underline') {
      styles.isUnderline = true;
    }
  });

  return styles;
};

const checkItalicStyle = (paragraphContent: Fragment & { content: TextNodeType[] }): boolean | undefined => {
  let hasAnyContent = false;
  let allItalic = true;

  for (const node of paragraphContent.content) {
    if (node.text && node.text.trim().length > 0) {
      hasAnyContent = true;
      const hasItalicOrEm = node.marks.some(mark => mark.type.name === 'italic' || mark.type.name === 'em');
      if (!hasItalicOrEm) {
        allItalic = false;
        break;
      }
    }
  }

  return hasAnyContent ? allItalic : undefined;
};

const checkRegularStyle = <K extends StyleProperty>(
  property: K,
  firstValue: UniformStyles[K],
  paragraphContent: Fragment & { content: TextNodeType[] }
): UniformStyles[K] | undefined => {
  let isUniform = true;

  for (const node of paragraphContent.content) {
    const nodeStyles = getStylesFromMarks(node.marks);
    if (nodeStyles[property] !== firstValue) {
      isUniform = false;
      break;
    }
  }

  return isUniform && firstValue !== undefined ? firstValue : undefined;
};

const findUniformStyles = (paragraphContent: Fragment & { content: TextNodeType[] }): UniformStyles => {
  if (paragraphContent.content.length === 0) return {};

  const firstStyles = getStylesFromMarks(paragraphContent.content[0].marks);
  const uniformStyles: UniformStyles = {};

  const checkProperty = <K extends StyleProperty>(property: K) => {
    if (property === 'isItalic') {
      const italicResult = checkItalicStyle(paragraphContent);
      if (italicResult !== undefined) {
        uniformStyles.isItalic = italicResult;
      }
      return;
    }

    const styleResult = checkRegularStyle(property, firstStyles[property], paragraphContent);
    if (styleResult !== undefined) {
      uniformStyles[property] = styleResult;
    }
  };

  ['fontSize', 'fontFamily', 'color'].forEach(prop =>
    checkProperty(prop as keyof Pick<UniformStyles, 'fontSize' | 'fontFamily' | 'color'>)
  );

  ['isBold', 'isItalic', 'isUnderline'].forEach(prop =>
    checkProperty(prop as keyof Pick<UniformStyles, 'isBold' | 'isItalic' | 'isUnderline'>)
  );

  return uniformStyles;
};

const getStyleFromPreviousListItem = (state: EditorState, pos: number): UniformStyles | null => {
  let previousListItem: ListItemNodeType | null = null;

  state.doc.nodesBetween(0, pos, (node, nodePos) => {
    if (node.type.name === 'listItem' && nodePos < pos) {
      previousListItem = node as ListItemNodeType;
    }
  });

  // Typescript bug workaround
  const _previousListItem = previousListItem as unknown as ListItemNodeType;

  if (_previousListItem?.attrs.storedStyles) {
    try {
      return JSON.parse(_previousListItem.attrs.storedStyles);
    } catch (e) {
      return null;
    }
  }

  return null;
};

const findNewListItemPositions = (transactions: readonly Transaction[]): number[] => {
  return transactions.reduce<number[]>((positions, transaction) => {
    transaction.steps.forEach(step => {
      const replaceStep = step as ReplaceStep;
      if (replaceStep.slice?.content?.firstChild?.type?.name === 'listItem' && replaceStep.from !== undefined) {
        positions.push(replaceStep.from);
      }
    });
    return positions;
  }, []);
};

const updateListItemStyles = (
  listItem: ListItemNodeType,
  newListItemPositions: number[],
  pos: number,
  state: EditorState
): UniformStyles => {
  let newStyles: UniformStyles = {};

  if (listItem.attrs.storedStyles) {
    try {
      newStyles = JSON.parse(listItem.attrs.storedStyles as string) as UniformStyles;
    } catch (e) {
      newStyles = {};
    }
  }

  if (newListItemPositions.includes(pos)) {
    const previousStyles = getStyleFromPreviousListItem(state, pos);
    if (previousStyles) {
      newStyles = previousStyles;
    }
  } else if (listItem.content.firstChild?.content.size > 0) {
    const currentUniformStyles = findUniformStyles(listItem.content.firstChild.content);

    (Object.keys(currentUniformStyles) as StyleProperty[]).forEach(<K extends StyleProperty>(key: K) => {
      if (currentUniformStyles[key] !== undefined) {
        newStyles[key] = currentUniformStyles[key] as UniformStyles[K];
      }
    });
  }

  return newStyles;
};

export const ListStylePropagation = Extension.create({
  name: 'listStylePropagation',

  addGlobalAttributes() {
    return [
      {
        types: ['listItem'],
        attributes: {
          storedStyles: {
            default: null,
            parseHTML: element => element.getAttribute('data-stored-styles'),
            renderHTML: attributes => {
              if (!attributes.storedStyles) {
                return {};
              }

              const styles = buildStyleString(JSON.parse(attributes.storedStyles));
              return {
                'data-stored-styles': attributes.storedStyles,
                style: styles
              };
            }
          }
        }
      }
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('listStylePropagation'),

        appendTransaction(transactions, oldState, newState) {
          const tr = newState.tr;
          const newListItemPositions = findNewListItemPositions(transactions);

          let hasChanges = false;
          newState.doc.descendants((node: Node, pos: number) => {
            if (node.type.name !== 'listItem') return;

            const listItem = node as ListItemNodeType;
            const paragraph = listItem.content.firstChild;
            if (paragraph?.type.name !== 'paragraph') return;

            const newStyles = updateListItemStyles(listItem, newListItemPositions, pos, newState);
            const newStylesString = JSON.stringify(newStyles);

            if (newStylesString === listItem.attrs.storedStyles) return;

            tr.setNodeMarkup(pos, null, {
              ...listItem.attrs,
              storedStyles: newStylesString
            });
            hasChanges = true;
          });

          return hasChanges ? tr : null;
        },

        props: {
          decorations: state => {
            const decorations: Decoration[] = [];

            state.doc.descendants((node: Node, pos: number) => {
              if (node.type.name !== 'listItem') return;

              const listItem = node as ListItemNodeType;
              if (!listItem.attrs.storedStyles) return;

              try {
                const styles = JSON.parse(listItem.attrs.storedStyles);
                decorations.push(
                  Decoration.node(pos, pos + listItem.nodeSize, {
                    style: buildStyleString(styles)
                  })
                );
              } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Error parsing stored styles', e);
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          }
        }
      })
    ];
  }
});
